import { getUnEmbeddedFonts, getUnEmbeddedFontsCount } from '@design-stack-ct/assets-sdk';
import { FileValidationErrorType } from 'src/components/UploadAndPrint/Uploader/useFileUpload';
import { UN_EMBEDDED_FONTS_PAGE_LIMIT } from './Constants';

export type PageData = {
  UnEmbeddedFontsCount: number;
};

export type UnEmbeddedFonts = {
  pages: Record<string, PageData>;
};

export async function validateUnEmbeddedFonts(file: File): Promise<Array<number>> {
  try {
    const unEmbeddedFontsInFile: Array<string> = await getUnEmbeddedFonts(file);

    /**
     * Validate only if there are un-embedded fonts in the PDF
     */
    if (unEmbeddedFontsInFile.length > 0) {
      const unEmbeddedFonts: UnEmbeddedFonts = await getUnEmbeddedFontsCount(file);
      const unEmbeddedFontPages = getUnEmbeddedFontPages(unEmbeddedFonts);
      if (unEmbeddedFontPages.length > 0) {
        return unEmbeddedFontPages;
      } else {
        throw new Error(
          JSON.stringify({
            type: FileValidationErrorType.UnEmbeddedFonts,
            message: 'Undetected un-embedded fonts in the file.',
            pages: [],
            bodyDescription: 'Please embed all fonts in your file before uploading.',
          })
        );
      }
    }
    return [];
  } catch (error) {
    throw new Error(
      JSON.stringify({
        type: FileValidationErrorType.UnEmbeddedFonts,
        message: 'Error validating un-embedded fonts.',
        pages: [],
        bodyDescription: 'Please embed all fonts in your file before uploading.',
      })
    );
  }
}

// This function returns an array of page numbers that contain un-embedded fonts in the user-uploaded PDF.
function getUnEmbeddedFontPages(unEmbeddedFonts: {
  pages: Record<string, { UnEmbeddedFontsCount: number }>;
}): number[] {
  return Object.keys(unEmbeddedFonts.pages).reduce<number[]>((acc, page) => {
    if (unEmbeddedFonts.pages[page].UnEmbeddedFontsCount !== 0) {
      acc.push(parseInt(page) + 1);
    }
    return acc;
  }, []);
}

/**
 * @param unEmbeddedPages - array of page numbers that contain un-embedded fonts in the user-uploaded PDF,
 * this can be undefined as well if the validation could not detect the pages with un-embedded fonts (Server side validation).
 * @returns A string representation of the page numbers that contain un-embedded fonts, limited to a maximum of 5 pages.
 */
export function getUnEmbeddedFilePages(unEmbeddedPages: number[] | undefined) {
  return unEmbeddedPages?.slice(0, UN_EMBEDDED_FONTS_PAGE_LIMIT).join(', ');
}
